@file:Suppress("MagicNumber")

package com.nonopn.skydome.common.app.pages.keraunic

import com.nonopn.skydome.common.app.api.impl.ApiInterface
import com.nonopn.skydome.common.app.pages.main.model.api.Project
import com.nonopn.skydome.common.app.pages.pdf.KeraunicMonthlyZone
import com.nonopn.skydome.common.app.pages.pdf.KeraunicNsgRef
import com.nonopn.skydome.common.app.pages.pdf.KeraunicReportCalculator
import com.nonopn.skydome.common.app.pages.pdf.KeraunicReportData
import com.nonopn.skydome.common.app.pages.pdf.KeraunicReportInput
import com.nonopn.skydome.common.app.pages.pdf.KeraunicTopInput
import com.nonopn.skywatcher.app.utils.safeLaunch
import com.nonopn.skywatcher.design.widgets.endOfDay
import com.nonopn.skywatcher.design.widgets.startOfDay
import com.nonopn.skywatcher.maps.LatLng
import eu.codlab.viewmodel.StateViewModel
import korlibs.time.DateTime
import korlibs.time.DateTimeTz
import korlibs.time.hours
import kotlinx.coroutines.delay

data class KeraunicReportModelState(
    val loading: Boolean = false,
    val error: Boolean = false,
    // Single computed content, rendered by the screen and by the PDF
    val data: KeraunicReportData? = null,
    val interpretationReady: Boolean = false,
    val observations: String = "",
    val title: String = "",
    val selectedFrom: DateTimeTz = (DateTime.now().local.startOfDay - (365 * 24).hours),
    val selectedTo: DateTimeTz = DateTime.now().local.endOfDay,
)

class KeraunicReportModel(
    private val project: Project,
    private val api: ApiInterface? = null,
) : StateViewModel<KeraunicReportModelState>(
    KeraunicReportModelState(title = defaultTitle(project.name))
) {
    companion object {
        /** Default report title (also the shared file name); it is not printed on the PDF cover. */
        fun defaultTitle(projectName: String) = "Keraunic Report — $projectName"

        /** Title typed by the user, printed on the PDF cover; empty when the default title is kept. */
        fun customTitle(title: String, projectName: String): String =
            title.trim().takeIf { it.isNotEmpty() && it != defaultTitle(projectName) } ?: ""
    }

    init {
        safeLaunch { load() }
    }

    fun load() = safeLaunch {
        updateState { copy(loading = true, error = false, interpretationReady = false) }

        try {
            val coordinates = LatLng(project.latitude, project.longitude)
            val selectedFrom = states.value.selectedFrom
            val selectedTo = states.value.selectedTo

            // 1. Nsg from /management/ng
            val ngData = api?.ng(coordinates)
            val nsg = ngData?.ng1year ?: 0.0

            // 2. Top impacts (and counts) from /management/probes/densities, LPS Manager projects only
            val densityData = api?.certificateDensityActivities(
                selectedFrom, selectedTo
            ).orEmpty()
            val section = densityData.firstOrNull { it.project.uuid == project.uuid }

            // 3. Full data from /skydome/report/keraunic (all events + daily/hourly graphs)
            val detail = try {
                api?.keraunicDetail(coordinates, selectedFrom, selectedTo)
            } catch (_: Throwable) {
                null
            }

            // Per-month zone stats: one /skydome/report/keraunic call per calendar month, only for an older
            // response (the dated flashes of the current response give the months directly)
            val monthlyZones = if (detail != null && !KeraunicReportCalculator.hasCompleteActivities(detail)) {
                loadMonthlyZones(coordinates, selectedFrom, selectedTo)
            } else {
                emptyList()
            }

            // Nsg of the closest significant cities (a failed city is skipped)
            val nsgRefs = KeraunicCities.nearest(project.latitude, project.longitude, 5)
                .mapNotNull { city ->
                    try {
                        api?.ng(LatLng(city.lat, city.lon))?.ng1year
                            ?.let { KeraunicNsgRef(city.name, it) }
                    } catch (_: Throwable) {
                        null
                    }
                }

            val data = KeraunicReportCalculator.compute(
                KeraunicReportInput(
                    latitude = project.latitude,
                    longitude = project.longitude,
                    fromSecondsUtc = selectedFrom.utc.unixMillisLong / 1000,
                    toSecondsUtc = selectedTo.utc.unixMillisLong / 1000,
                    periodLabel = "${dayLabel(selectedFrom)} — ${dayLabel(selectedTo)}",
                    nsg = nsg,
                    nsgRefs = nsgRefs,
                    detail = detail,
                    densityLess1Km = section?.less1Km,
                    densityLess2Km = section?.less2Km,
                    topImpacts = section?.topActivity.orEmpty().map { top ->
                        val act = top.activity
                        KeraunicTopInput(
                            externalId = act.externalId,
                            dateLabel = dayLabel(act.recDateTime),
                            atSecondsUtc = act.recTimestampSeconds,
                            distKm = act.distance(coordinates),
                            latitude = act.latitude,
                            longitude = act.longitude,
                            peakAmp = act.pulsePeakAmp,
                            sensorCount = act.pulseNumSensors,
                            percent = top.percent,
                        )
                    },
                    monthlyZones = monthlyZones,
                    // Impact times are shown in the device time zone, with its UTC offset
                    utcOffsetMinutes = { seconds -> DateTime.fromUnixMillis(seconds * 1000).local.offset.totalMinutesInt },
                )
            )

            updateState { copy(loading = false, data = data) }
            delay(800)
            updateState { copy(interpretationReady = true) }
        } catch (_: Throwable) {
            updateState { copy(loading = false, error = true) }
        }
    }

    // Splits the period in calendar months (clipped to the period) and requests each one.
    // A month without impacts or a failed call is skipped (counts as zero).
    private suspend fun loadMonthlyZones(
        coordinates: LatLng,
        from: DateTimeTz,
        to: DateTimeTz,
    ): List<KeraunicMonthlyZone> {
        val fromSec = from.utc.unixMillisLong / 1000
        val toSec = to.utc.unixMillisLong / 1000
        var year = from.yearInt
        var month = from.month1
        val result = mutableListOf<KeraunicMonthlyZone>()

        while (true) {
            val monthStart = DateTime(year, month, 1).unixMillisLong / 1000
            val nextYear = if (month == 12) year + 1 else year
            val nextMonth = if (month == 12) 1 else month + 1
            val nextStart = DateTime(nextYear, nextMonth, 1).unixMillisLong / 1000
            if (monthStart >= toSec) break

            val windowFrom = maxOf(fromSec, monthStart)
            val windowTo = minOf(toSec, nextStart)
            val monthDetail = try {
                api?.keraunicDetail(
                    coordinates,
                    DateTime.fromUnixMillis(windowFrom * 1000).local,
                    DateTime.fromUnixMillis(windowTo * 1000).local,
                )
            } catch (_: Throwable) {
                null
            }
            if (monthDetail != null) {
                result.add(
                    KeraunicMonthlyZone(month, monthDetail.eventsAt1Km, monthDetail.eventsAt2Km)
                )
            }
            year = nextYear
            month = nextMonth
        }
        return result
    }

    // dd/MM/yyyy in the device time zone
    private fun dayLabel(date: DateTimeTz) =
        "${date.dayOfMonth.toString().padStart(2, '0')}/${date.month1.toString().padStart(2, '0')}/${date.yearInt}"

    fun setTitle(title: String) = updateState { copy(title = title) }

    fun setObservations(obs: String) = updateState { copy(observations = obs) }

    fun setFrom(date: DateTimeTz) {
        updateState { copy(selectedFrom = date) }
        load()
    }

    fun setTo(date: DateTimeTz) {
        updateState { copy(selectedTo = date) }
        load()
    }
}
